import { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { StatisticNodeMetadata } from '../../explorer/models/nodeTypes';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Statistic-specific context menu action types
 */
type StatisticContextMenuActionType =
  | 'copy-name'
  | 'update-statistics'
  | 'script-statistic-as'
  | 'script-statistic-create'
  | 'script-statistic-drop';

const StatisticActions = {
  COPY_NAME: 'copy-name' as StatisticContextMenuActionType,
  UPDATE_STATISTICS: 'update-statistics' as StatisticContextMenuActionType,
  SCRIPT_STATISTIC_AS: 'script-statistic-as' as StatisticContextMenuActionType,
  SCRIPT_STATISTIC_CREATE: 'script-statistic-create' as StatisticContextMenuActionType,
  SCRIPT_STATISTIC_DROP: 'script-statistic-drop' as StatisticContextMenuActionType,
};

/**
 * Generate context menu actions for a statistic node
 */
export function generateStatisticContextMenuActions(): ContextMenuAction[] {
  return [
    { id: StatisticActions.COPY_NAME, label: 'Copy statistic name', icon: 'clipboard' },
    { id: StatisticActions.UPDATE_STATISTICS, label: 'Update Statistics', icon: 'refresh' },
    {
      id: StatisticActions.SCRIPT_STATISTIC_AS,
      label: 'Script as',
      icon: 'code',
      submenu: [
        { id: StatisticActions.SCRIPT_STATISTIC_CREATE, label: 'CREATE', icon: 'file-plus' },
        { id: StatisticActions.SCRIPT_STATISTIC_DROP, label: 'DROP', icon: 'trash' },
      ],
    },
  ];
}

/**
 * Generate UPDATE STATISTICS DDL (engine-aware)
 */
function generateUpdateStatisticsDdl(
  metadata: StatisticNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const fullTable = qualifyTable(metadata.schema, metadata.tableName);

  if (engine === 'mssql') {
    return `UPDATE STATISTICS ${fullTable} ${quoteId(metadata.statisticName)};\n`;
  }

  // PostgreSQL — ANALYZE table
  return `ANALYZE ${fullTable};\n`;
}

/**
 * Generate CREATE STATISTICS DDL (engine-aware)
 */
export function generateStatisticCreateDdl(
  metadata: StatisticNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const fullTable = qualifyTable(metadata.schema, metadata.tableName);
  const cols = metadata.columns.map(c => quoteId(c)).join(', ');

  if (engine === 'mssql') {
    return `CREATE STATISTICS ${quoteId(metadata.statisticName)}\n    ON ${fullTable} (${cols});\n`;
  }

  // PostgreSQL — CREATE STATISTICS with ndistinct, dependencies, mcv (schema-qualified)
  const qualifiedStat = qualifyTable(metadata.schema, metadata.statisticName);
  return `CREATE STATISTICS ${qualifiedStat} (ndistinct, dependencies, mcv)\n    ON ${cols}\n    FROM ${fullTable};\n`;
}

/**
 * Generate DROP STATISTICS DDL (engine-aware)
 */
export function generateStatisticDropDdl(
  metadata: StatisticNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  if (engine === 'mssql') {
    const fullTable = qualifyTable(metadata.schema, metadata.tableName);
    return `DROP STATISTICS ${fullTable}.${quoteId(metadata.statisticName)};\n`;
  }

  // PostgreSQL — schema-qualify for non-search_path schemas
  const qualifiedStat = qualifyTable(metadata.schema, metadata.statisticName);
  return `DROP STATISTICS ${qualifiedStat};\n`;
}

/**
 * Execute a statistic context menu action
 */
export function executeStatisticContextMenuAction(
  actionId: string,
  metadata: StatisticNodeMetadata,
): void {
  if (actionId === StatisticActions.COPY_NAME) {
    void writeClipboardText(metadata.statisticName);
    return;
  }

  if (
    actionId === StatisticActions.UPDATE_STATISTICS ||
    actionId === StatisticActions.SCRIPT_STATISTIC_CREATE ||
    actionId === StatisticActions.SCRIPT_STATISTIC_DROP
  ) {
    void handleStatisticScript(metadata, actionId);
  }
}

/**
 * Generate statistic script and open in a new query tab
 */
async function handleStatisticScript(
  metadata: StatisticNodeMetadata,
  actionId: string,
): Promise<void> {
  const { getScriptProvider } = await import('../../scripting');
  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');

  const { activeConnection, connection, engine } = getConnectionById(metadata.connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const provider = getScriptProvider(engine);
  const quoteId = (name: string) => provider.quoteIdentifier(name);
  const qualifyTable = (schema: string, table: string) => provider.qualifyTableName(schema, table);

  let script: string;
  let titleVerb: string;

  if (actionId === StatisticActions.UPDATE_STATISTICS) {
    script = generateUpdateStatisticsDdl(metadata, engine, quoteId, qualifyTable);
    titleVerb = 'UPDATE';
  } else if (actionId === StatisticActions.SCRIPT_STATISTIC_CREATE) {
    script = generateStatisticCreateDdl(metadata, engine, quoteId, qualifyTable);
    titleVerb = 'CREATE';
  } else {
    script = generateStatisticDropDdl(metadata, engine, quoteId, qualifyTable);
    titleVerb = 'DROP';
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${metadata.statisticName}_${titleVerb}-${queryNumber}`,
    type: 'query',
    content: { query: script },
    connectionInfo: {
      connectionId: metadata.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: metadata.database,
    },
  });
}
